import { useCallback } from 'react';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { ReportTypesV2 } from 'src/utils/reportsUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';

import {
  ReportConfigV2,
  useReportConfigsV2Internal,
} from './useReportConfigsV2Internal';

export function useReportConfigActionsV2() {
  const { showErrorDialog } = useClientLogContext();

  const {
    deleteReportConfig: deleteReportConfigInventory,
    upsertReportConfig: upsertReportConfigInventory,
    getReportConfigNameForDuplicate: getReportConfigNameForDuplicateInventory,
  } = useReportConfigsV2Internal<ReportTypesV2.InventoryV2>({
    reportType: ReportTypesV2.InventoryV2,
  });
  const {
    deleteReportConfig: deleteReportConfigSale,
    upsertReportConfig: upsertReportConfigSale,
    getReportConfigNameForDuplicate: getReportConfigNameForDuplicateSale,
  } = useReportConfigsV2Internal<ReportTypesV2.SaleV2>({
    reportType: ReportTypesV2.SaleV2,
  });

  const {
    deleteReportConfig: deleteReportConfigPurchase,
    upsertReportConfig: upsertReportConfigPurchase,
    getReportConfigNameForDuplicate: getReportConfigNameForDuplicatePurchase,
  } = useReportConfigsV2Internal<ReportTypesV2.Purchase>({
    reportType: ReportTypesV2.Purchase,
  });

  const {
    deleteReportConfig: deleteReportConfigSaleLineItem,
    upsertReportConfig: upsertReportConfigSaleLineItem,
    getReportConfigNameForDuplicate:
      getReportConfigNameForDuplicateSaleLineItem,
  } = useReportConfigsV2Internal<ReportTypesV2.SaleLineItem>({
    reportType: ReportTypesV2.SaleLineItem,
  });

  const {
    deleteReportConfig: deleteReportConfigSoldTicket,
    upsertReportConfig: upsertReportConfigSoldTicket,
    getReportConfigNameForDuplicate: getReportConfigNameForDuplicateSoldTicket,
  } = useReportConfigsV2Internal<ReportTypesV2.SoldTicket>({
    reportType: ReportTypesV2.SoldTicket,
  });

  const {
    deleteReportConfig: deleteReportConfigPurchaseLineItem,
    upsertReportConfig: upsertReportConfigPurchaseLineItem,
    getReportConfigNameForDuplicate:
      getReportConfigNameForDuplicatePurchaseLineItem,
  } = useReportConfigsV2Internal<ReportTypesV2.PurchaseOrderLineItem>({
    reportType: ReportTypesV2.PurchaseOrderLineItem,
  });

  const {
    deleteReportConfig: deleteReportConfigCostOfGoodsSold,
    upsertReportConfig: upsertReportConfigCostOfGoodsSold,
    getReportConfigNameForDuplicate:
      getReportConfigNameForDuplicateCostOfGoodsSold,
  } = useReportConfigsV2Internal<ReportTypesV2.CostOfGoodsSold>({
    reportType: ReportTypesV2.CostOfGoodsSold,
  });

  const {
    deleteReportConfig: deleteReportConfigSaleCostAdjustments,
    upsertReportConfig: upsertReportConfigSaleCostAdjustments,
    getReportConfigNameForDuplicate:
      getReportConfigNameForDuplicateSaleCostAdjustments,
  } = useReportConfigsV2Internal<ReportTypesV2.SaleCostAdjustments>({
    reportType: ReportTypesV2.SaleCostAdjustments,
  });

  const {
    deleteReportConfig: deleteReportConfigTicketGroup,
    upsertReportConfig: upsertReportConfigTicketGroup,
    getReportConfigNameForDuplicate: getReportConfigNameForDuplicateTicketGroup,
  } = useReportConfigsV2Internal<ReportTypesV2.TicketGroup>({
    reportType: ReportTypesV2.TicketGroup,
  });

  const upsertReportConfig = useCallback(
    async (report: ReportConfigV2) => {
      return await tryInvokeApi(
        async () => {
          if (report.reportType === ReportTypesV2.InventoryV2) {
            return await upsertReportConfigInventory(report.reportId, report);
          } else if (report.reportType === ReportTypesV2.SaleV2) {
            return await upsertReportConfigSale(report.reportId, report);
          } else if (report.reportType === ReportTypesV2.Purchase) {
            return await upsertReportConfigPurchase(report.reportId, report);
          } else if (report.reportType === ReportTypesV2.SaleLineItem) {
            return await upsertReportConfigSaleLineItem(
              report.reportId,
              report
            );
          } else if (report.reportType === ReportTypesV2.SoldTicket) {
            return await upsertReportConfigSoldTicket(report.reportId, report);
          } else if (
            report.reportType === ReportTypesV2.PurchaseOrderLineItem
          ) {
            return await upsertReportConfigPurchaseLineItem(
              report.reportId,
              report
            );
          } else if (report.reportType === ReportTypesV2.CostOfGoodsSold) {
            return await upsertReportConfigCostOfGoodsSold(
              report.reportId,
              report
            );
          } else if (report.reportType === ReportTypesV2.SaleCostAdjustments) {
            return await upsertReportConfigSaleCostAdjustments(
              report.reportId,
              report
            );
          } else if (report.reportType === ReportTypesV2.TicketGroup) {
            return await upsertReportConfigTicketGroup(report.reportId, report);
          }
        },
        (error) => {
          showErrorDialog('ReportV2Client.mergeReport', error, {
            trackErrorData: report,
          });
        }
      );
    },
    [
      upsertReportConfigInventory,
      upsertReportConfigSale,
      upsertReportConfigPurchase,
      upsertReportConfigSaleLineItem,
      upsertReportConfigSoldTicket,
      upsertReportConfigPurchaseLineItem,
      upsertReportConfigCostOfGoodsSold,
      upsertReportConfigSaleCostAdjustments,
      upsertReportConfigTicketGroup,
      showErrorDialog,
    ]
  );

  const deleteReportConfig = useCallback(
    async (reportType: ReportTypesV2, reportId: number) => {
      await tryInvokeApi(
        async () => {
          if (reportType === ReportTypesV2.InventoryV2) {
            deleteReportConfigInventory(reportId);
          } else if (reportType === ReportTypesV2.SaleV2) {
            deleteReportConfigSale(reportId);
          } else if (reportType === ReportTypesV2.Purchase) {
            deleteReportConfigPurchase(reportId);
          } else if (reportType === ReportTypesV2.SaleLineItem) {
            deleteReportConfigSaleLineItem(reportId);
          } else if (reportType === ReportTypesV2.SoldTicket) {
            deleteReportConfigSoldTicket(reportId);
          } else if (reportType === ReportTypesV2.PurchaseOrderLineItem) {
            deleteReportConfigPurchaseLineItem(reportId);
          } else if (reportType === ReportTypesV2.CostOfGoodsSold) {
            deleteReportConfigCostOfGoodsSold(reportId);
          } else if (reportType === ReportTypesV2.SaleCostAdjustments) {
            deleteReportConfigSaleCostAdjustments(reportId);
          } else if (reportType === ReportTypesV2.TicketGroup) {
            deleteReportConfigTicketGroup(reportId);
          }
        },
        (error) => {
          showErrorDialog('UserSetting.deleteReport', error, {
            trackErrorData: {
              reportType,
              reportId,
            },
          });
        }
      );
    },
    [
      deleteReportConfigInventory,
      deleteReportConfigSale,
      deleteReportConfigPurchase,
      deleteReportConfigSaleLineItem,
      deleteReportConfigSoldTicket,
      deleteReportConfigPurchaseLineItem,
      deleteReportConfigCostOfGoodsSold,
      deleteReportConfigSaleCostAdjustments,
      deleteReportConfigTicketGroup,
      showErrorDialog,
    ]
  );
  const getReportConfigNameForDuplicate = useCallback(
    (reportType: ReportTypesV2, reportName: string) => {
      if (reportType === ReportTypesV2.InventoryV2) {
        return getReportConfigNameForDuplicateInventory(reportName);
      } else if (reportType === ReportTypesV2.SaleV2) {
        return getReportConfigNameForDuplicateSale(reportName);
      } else if (reportType === ReportTypesV2.Purchase) {
        return getReportConfigNameForDuplicatePurchase(reportName);
      } else if (reportType === ReportTypesV2.SaleLineItem) {
        return getReportConfigNameForDuplicateSaleLineItem(reportName);
      } else if (reportType === ReportTypesV2.PurchaseOrderLineItem) {
        return getReportConfigNameForDuplicatePurchaseLineItem(reportName);
      } else if (reportType === ReportTypesV2.CostOfGoodsSold) {
        return getReportConfigNameForDuplicateCostOfGoodsSold(reportName);
      } else if (reportType === ReportTypesV2.SaleCostAdjustments) {
        return getReportConfigNameForDuplicateSaleCostAdjustments(reportName);
      } else if (reportType === ReportTypesV2.TicketGroup) {
        return getReportConfigNameForDuplicateTicketGroup(reportName);
      }
      // reportType === ReportTypesV2.SoldTicket
      return getReportConfigNameForDuplicateSoldTicket(reportName);
    },
    [
      getReportConfigNameForDuplicateSoldTicket,
      getReportConfigNameForDuplicateInventory,
      getReportConfigNameForDuplicateSale,
      getReportConfigNameForDuplicatePurchase,
      getReportConfigNameForDuplicateSaleLineItem,
      getReportConfigNameForDuplicatePurchaseLineItem,
      getReportConfigNameForDuplicateCostOfGoodsSold,
      getReportConfigNameForDuplicateSaleCostAdjustments,
      getReportConfigNameForDuplicateTicketGroup,
    ]
  );

  return {
    upsertReportConfig,
    deleteReportConfig,
    getReportConfigNameForDuplicate,
  };
}
